import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppLanguage } from '../../../Hooks/useAppLanguage';
import AuthContext from '../../../Context/AuthContext';
import { ordersAPI } from '../../../Api/api';
import Button from '../../../Component/UI/Button/Button';
import ProductCardImage from '../../../Component/UI/ProductCard/ProductCardImage';
import {
    formatOrderItemVariantLabel,
    formatOrderMoney,
    getProductOrderStatusClass,
    getProductOrderStatusStep,
    resolveOrderItemName,
} from '../../../Utils/orderDisplay';
import './TrackOrder.css';

const TrackOrder = () => {
    const { t, currentLanguage, dir, langPath } = useAppLanguage();
    const { user } = useContext(AuthContext);
    const [searchParams, setSearchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState('orderNumber');
    const [orderNumber, setOrderNumber] = useState(searchParams.get('orderNumber') || '');
    const [orderDetails, setOrderDetails] = useState(null);
    const [searchForm, setSearchForm] = useState({ email: '', phone: '' });
    const [searchResults, setSearchResults] = useState([]);
    const [userOrders, setUserOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const getStatusText = (status) => {
        const key = status?.toLowerCase?.();
        const map = {
            'awaiting-payment': t('myOrders.statusAwaitingPayment'),
            pending: t('myOrders.statusPending'),
            confirmed: t('myOrders.statusConfirmed'),
            processing: t('myOrders.statusProcessing'),
            shipped: t('myOrders.statusShipped'),
            delivered: t('myOrders.statusDelivered'),
            completed: t('myOrders.statusCompleted'),
            cancelled: t('myOrders.statusCancelled'),
            refunded: t('myOrders.statusRefunded'),
        };
        return map[key] || status;
    };

    const getPaymentMethodLabel = (method) => {
        const key = method?.toLowerCase?.();
        const map = {
            cash: t('orderConfirmation.paymentCash'),
            card: t('orderConfirmation.paymentCard'),
            'bank-transfer': t('orderConfirmation.paymentBankTransfer'),
            online: t('orderConfirmation.paymentOnline'),
            wallet: t('orderConfirmation.paymentWallet'),
        };
        return map[key] || method || '—';
    };

    const handleTrackOrder = useCallback(async (orderNum = orderNumber) => {
        if (!orderNum.trim()) {
            setError(t('trackOrder.enterOrderNumber'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await ordersAPI.getOrderByNumber(orderNum.trim());
            if (response?.data?.status === 'success') {
                setOrderDetails(response.data.data);
                setSearchParams({ orderNumber: orderNum.trim() });
            } else {
                setError(response?.data?.message || t('trackOrder.orderNotFound'));
                setOrderDetails(null);
            }
        } catch (err) {
            setError(err?.response?.data?.message || t('trackOrder.orderNotFound'));
            setOrderDetails(null);
        } finally {
            setLoading(false);
        }
    }, [orderNumber, setSearchParams, t]);

    const handleSearchOrders = async (e) => {
        e.preventDefault();

        if (!searchForm.email && !searchForm.phone) {
            setError(t('trackOrder.provideEmailOrPhone'));
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await ordersAPI.searchByContact(searchForm);
            if (response?.data?.status === 'success') {
                const results = response.data.data || [];
                setSearchResults(results);
                if (results.length === 0) {
                    setError(t('trackOrder.noOrdersFound'));
                }
            } else {
                setError(response?.data?.message || t('trackOrder.searchFailed'));
                setSearchResults([]);
            }
        } catch (err) {
            setError(err?.response?.data?.message || t('trackOrder.searchFailed'));
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    const loadUserOrderHistory = useCallback(async () => {
        if (!user?._id) return;

        setLoading(true);
        setError(null);

        try {
            const response = await ordersAPI.getUserOrderHistory();
            if (response?.data?.status === 'success') {
                setUserOrders(response.data.data || []);
            } else {
                setError(response?.data?.message || t('trackOrder.loadHistoryFailed'));
            }
        } catch (err) {
            setError(err?.response?.data?.message || t('trackOrder.loadHistoryFailed'));
        } finally {
            setLoading(false);
        }
    }, [user?._id, t]);

    const handleOrderNumberSubmit = (e) => {
        e.preventDefault();
        handleTrackOrder();
    };

    const handleSearchInputChange = (e) => {
        const { name, value } = e.target;
        setSearchForm((prev) => ({ ...prev, [name]: value }));
    };

    useEffect(() => {
        const param = searchParams.get('orderNumber');
        if (param) {
            handleTrackOrder(param);
        }
    }, [searchParams, handleTrackOrder]);

    useEffect(() => {
        if (activeTab === 'userOrders' && user?._id && userOrders.length === 0) {
            loadUserOrderHistory();
        }
    }, [activeTab, user?._id, loadUserOrderHistory, userOrders.length]);

    const renderSnippetCard = (order, key) => (
        <button
            type="button"
            key={key}
            className="track-snippet-card"
            onClick={() => setOrderDetails(order)}
        >
            <div className="track-snippet-head">
                <span className="track-snippet-number">#{order.orderNumber}</span>
                <span className={`track-snippet-status ${getProductOrderStatusClass(order.orderStatus)}`}>
                    {getStatusText(order.orderStatus)}
                </span>
            </div>
            <div className="track-snippet-meta">
                <span>{new Date(order.createdAt).toLocaleDateString(currentLanguage)}</span>
                <span className="track-snippet-total">
                    {formatOrderMoney(order.totalAmount, order.currency)}
                </span>
            </div>
            <div className="track-snippet-preview">
                {(order.items?.slice(0, 2) ?? []).map((item, idx, arr) => (
                    <span key={idx}>
                        {resolveOrderItemName(item.name, currentLanguage)}
                        {idx < arr.length - 1 && ', '}
                    </span>
                ))}
                {(order.items?.length ?? 0) > 2 && ` +${order.items.length - 2} ${t('trackOrder.more')}`}
            </div>
        </button>
    );

    const shippingAddress = orderDetails?.customerInfo?.shippingAddress;
    const statusStep = getProductOrderStatusStep(orderDetails?.orderStatus);

    return (
        <main className="main">
            <section className="track-order-page">
                <div className="container" dir={dir}>
                    <div className="track-order-panel">
                        <div className="track-order-tabs" role="tablist" aria-label={t('trackOrder.title')}>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'orderNumber'}
                                className={`track-order-tab ${activeTab === 'orderNumber' ? 'track-order-tab--active' : ''}`}
                                onClick={() => setActiveTab('orderNumber')}
                            >
                                {t('trackOrder.trackByOrderNumber')}
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={activeTab === 'search'}
                                className={`track-order-tab ${activeTab === 'search' ? 'track-order-tab--active' : ''}`}
                                onClick={() => setActiveTab('search')}
                            >
                                {t('trackOrder.searchByContact')}
                            </button>
                            {user && (
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'userOrders'}
                                    className={`track-order-tab ${activeTab === 'userOrders' ? 'track-order-tab--active' : ''}`}
                                    onClick={() => setActiveTab('userOrders')}
                                >
                                    {t('trackOrder.myOrders')}
                                </button>
                            )}
                        </div>

                        <div className="track-order-tab-panel">
                            {activeTab === 'orderNumber' && (
                                <form className="track-order-form" onSubmit={handleOrderNumberSubmit}>
                                    <div className="track-form-group">
                                        <label htmlFor="orderNumber">{t('trackOrder.orderNumber')}</label>
                                        <input
                                            type="text"
                                            id="orderNumber"
                                            value={orderNumber}
                                            onChange={(e) => setOrderNumber(e.target.value)}
                                            placeholder={t('trackOrder.enterOrderNumber')}
                                            required
                                        />
                                    </div>
                                    <Button type="submit" className="track-order-submit" disabled={loading}>
                                        {loading ? t('trackOrder.tracking') : t('trackOrder.trackOrder')}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'search' && (
                                <form className="track-order-form" onSubmit={handleSearchOrders}>
                                    <div className="track-form-group">
                                        <label htmlFor="email">{t('trackOrder.email')}</label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            value={searchForm.email}
                                            onChange={handleSearchInputChange}
                                            placeholder={t('trackOrder.enterEmail')}
                                        />
                                    </div>
                                    <div className="track-form-group">
                                        <label htmlFor="phone">{t('trackOrder.phone')}</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={searchForm.phone}
                                            onChange={handleSearchInputChange}
                                            placeholder={t('trackOrder.enterPhone')}
                                        />
                                    </div>
                                    <Button type="submit" className="track-order-submit" disabled={loading}>
                                        {loading ? t('trackOrder.searching') : t('trackOrder.searchOrders')}
                                    </Button>
                                </form>
                            )}

                            {activeTab === 'userOrders' && user && (
                                <div className="track-user-orders">
                                    <h2 className="track-subsection-title">{t('trackOrder.recentOrders')}</h2>
                                    {loading ? (
                                        <div className="track-order-loading">
                                            <div className="track-order-spinner" aria-hidden />
                                            <p>{t('trackOrder.loading')}</p>
                                        </div>
                                    ) : userOrders.length === 0 ? (
                                        <div className="track-order-empty">
                                            <p>{t('trackOrder.noOrders')}</p>
                                            <Button to={langPath('/products')}>
                                                {t('trackOrder.startOrdering')}
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="track-snippet-list">
                                            {userOrders.map((order) => renderSnippetCard(order, order._id))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {error && (
                            <div className="track-order-error" role="alert">
                                <p>{error}</p>
                            </div>
                        )}

                        {activeTab === 'search' && searchResults.length > 0 && (
                            <div className="track-search-results">
                                <h2 className="track-subsection-title">{t('trackOrder.searchResults')}</h2>
                                <div className="track-snippet-list">
                                    {searchResults.map((order) => renderSnippetCard(order, order._id))}
                                </div>
                            </div>
                        )}

                        {orderDetails && (
                            <div className="track-detail">
                                <div className="track-detail-hero">
                                    <h2 className="track-detail-title">{t('trackOrder.orderDetails')}</h2>
                                    <div className="track-detail-row track-detail-row--highlight">
                                        <span className="track-detail-k">{t('trackOrder.orderNumber')}</span>
                                        <span className="track-detail-v">{orderDetails.orderNumber}</span>
                                    </div>
                                    <div className="track-detail-row">
                                        <span className="track-detail-k">{t('trackOrder.status')}</span>
                                        <span className={`track-detail-badge ${getProductOrderStatusClass(orderDetails.orderStatus)}`}>
                                            {getStatusText(orderDetails.orderStatus)}
                                        </span>
                                    </div>
                                </div>

                                {statusStep > 0 && (
                                    <div className="track-timeline-block">
                                        <h3 className="track-detail-h3">{t('trackOrder.orderProgress')}</h3>
                                        <div className="track-timeline">
                                            {[
                                                { step: 1, title: t('trackOrder.pending'), desc: t('trackOrder.pendingDesc') },
                                                { step: 2, title: t('trackOrder.confirmed'), desc: t('trackOrder.confirmedDesc') },
                                                { step: 3, title: t('trackOrder.processing'), desc: t('trackOrder.processingDesc') },
                                                { step: 4, title: t('trackOrder.shipped'), desc: t('trackOrder.shippedDesc') },
                                                { step: 5, title: t('trackOrder.delivered'), desc: t('trackOrder.deliveredDesc') },
                                            ].map(({ step, title, desc }) => (
                                                <div
                                                    key={step}
                                                    className={`track-timeline-step ${statusStep >= step ? 'track-timeline-step--active' : ''}`}
                                                >
                                                    <div className="track-timeline-circle">{step}</div>
                                                    <div className="track-timeline-body">
                                                        <h4>{title}</h4>
                                                        <p>{desc}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="track-detail-section">
                                    <h3 className="track-detail-h3">{t('trackOrder.orderSummary')}</h3>
                                    <div className="track-summary-grid">
                                        <div className="track-summary-cell">
                                            <span className="track-summary-k">{t('trackOrder.paymentMethod')}</span>
                                            <span className="track-summary-v">
                                                {getPaymentMethodLabel(orderDetails.payment?.paymentMethod)}
                                            </span>
                                        </div>
                                        <div className="track-summary-cell">
                                            <span className="track-summary-k">{t('trackOrder.totalAmount')}</span>
                                            <span className="track-summary-v track-summary-v--accent">
                                                {formatOrderMoney(orderDetails.totalAmount, orderDetails.currency)}
                                            </span>
                                        </div>
                                        {orderDetails.shipping?.estimatedDeliveryDate && (
                                            <div className="track-summary-cell">
                                                <span className="track-summary-k">{t('trackOrder.estimatedTime')}</span>
                                                <span className="track-summary-v">
                                                    {new Date(orderDetails.shipping.estimatedDeliveryDate).toLocaleDateString(currentLanguage)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="track-detail-section">
                                    <h3 className="track-detail-h3">{t('trackOrder.orderItems')}</h3>
                                    <div className="track-items-list">
                                        {orderDetails.items?.map((item, index) => {
                                            const variantLabel = formatOrderItemVariantLabel(item);
                                            return (
                                            <div key={index} className="track-item-row">
                                                <div className="track-item-info">
                                                    <div className="track-item-thumb">
                                                        <ProductCardImage
                                                            product={{
                                                                _id: item.productId,
                                                                imageUrl: item.primaryImage,
                                                                primaryImage: item.primaryImage,
                                                            }}
                                                            productName={resolveOrderItemName(item.name, currentLanguage)}
                                                            alt={resolveOrderItemName(item.name, currentLanguage)}
                                                            className="track-item-img"
                                                            compact
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="track-item-name">
                                                            {resolveOrderItemName(item.name, currentLanguage)}
                                                        </div>
                                                        {variantLabel ? (
                                                            <div className="track-item-variant">
                                                                {variantLabel}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                                <span className="track-item-qty">×{item.quantity}</span>
                                                <span className="track-item-price">
                                                    {formatOrderMoney(item.price, orderDetails.currency)}
                                                </span>
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {shippingAddress && (
                                    <div className="track-detail-section">
                                        <h3 className="track-detail-h3">{t('trackOrder.shippingAddress')}</h3>
                                        <div className="track-address">
                                            {shippingAddress.street}
                                            {shippingAddress.apartment ? `, ${shippingAddress.apartment}` : ''}
                                            <br />
                                            {[shippingAddress.city, shippingAddress.state, shippingAddress.postalCode]
                                                .filter(Boolean)
                                                .join(', ')}
                                            {shippingAddress.country ? (
                                                <>
                                                    <br />
                                                    {shippingAddress.country}
                                                </>
                                            ) : null}
                                        </div>
                                    </div>
                                )}

                                {orderDetails.customerNotes && (
                                    <div className="track-detail-section">
                                        <h3 className="track-detail-h3">{t('trackOrder.specialInstructions')}</h3>
                                        <p className="track-notes">{orderDetails.customerNotes}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="track-order-footer">
                            <Button to={langPath('/products')}>
                                {t('trackOrder.continueShopping')}
                            </Button>
                            <Button to={langPath('/contact')} variant="outline">
                                {t('trackOrder.needHelp')}
                            </Button>
                            {user && (
                                <Button to={langPath('/my-orders')} variant="outline">
                                    {t('trackOrder.myOrders')}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default TrackOrder;
